import React from 'react';

export interface IStepLabel {
	label: string;
	hidden?: boolean;
}

interface ISteps {
	// 1-indexed number representing current step
	currentStep: number;
	steps: IStepLabel[];
	onStepClick?: (step: number) => void;
	disabled?: boolean;
}

const getLabelClass = (
	targetStepIndex: number,
	currentStep: number,
): string => {
	if (targetStepIndex < currentStep) {
		return 'complete';
	}
	if (targetStepIndex === currentStep) {
		return 'current';
	}
	if (targetStepIndex > currentStep) {
		return 'incomplete';
	}

	throw new Error('Could not determine step class');
};

const Steps: React.FC<ISteps> = ({
	currentStep,
	steps,
	onStepClick,
	disabled,
}) => {
	const visibleSteps = steps.filter(step => !step.hidden);

	return (
		<nav className="steps" aria-label="study plan steps">
			{visibleSteps.map(({ label }, labelIndex) => [
				// add 1 to label index to make it 1-indexed
				<div
					key={label}
					className={`step-label ${getLabelClass(
						labelIndex + 1,
						currentStep,
					)}`}
					onClick={() => {
						if (disabled) {
							return;
						}
						onStepClick?.(labelIndex + 1);
					}}
					onKeyDown={e => {
						if (disabled) {
							return;
						}
						if (e.key === 'Enter') {
							onStepClick?.(labelIndex + 1);
						}
					}}
					tabIndex={0}
					role="button"
					aria-label={`Step${labelIndex + 1}: ${label}`}
					data-testid={`${label.split(' ').join('-')}-step`}
				>
					<p className="step-number">{labelIndex + 1}</p>
					<p id={`${label.split(' ').join('-')}-step`}>{label}</p>
				</div>,
				labelIndex !== visibleSteps.length - 1 ? (
					<div key={`separator-${label}`} className="separator" />
				) : null,
			])}
		</nav>
	);
};

export default Steps;
